import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MILESTONE_STATS } from '../store';
import { TRAINING_GOAL, todayIso } from '../utils';
import { PMS } from '@/features/shared/pms';

function Modal({
  label,
  onCancel,
  large,
  children,
}: {
  label: string;
  onCancel: () => void;
  large?: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const focusable = el?.querySelector<HTMLElement>(
      'button, [href], input, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onCancel]);
  return (
    <div className="clm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div
        ref={ref}
        className={`clm-dialog${large ? ' clm-dialog-lg' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </div>
    </div>
  );
}

// ---- Hold -------------------------------------------------------------------

const HOLD_REASONS = [
  'Waiting for a document',
  'Waiting for the dentist',
  'Checking with the patient or their insurance',
  'Something else',
];

export function HoldDialog({
  prefillReason,
  prefillNote,
  onCancel,
  onHold,
}: {
  prefillReason?: string;
  prefillNote?: string;
  onCancel: () => void;
  onHold: (v: { reason: string; note?: string; until?: string }) => void;
}) {
  const [reason, setReason] = useState(prefillReason ?? '');
  const [note, setNote] = useState(prefillNote ?? '');
  const [until, setUntil] = useState('');
  const min = todayIso('training');

  return (
    <Modal label="Hold this claim" onCancel={onCancel}>
      <h2 className="clm-dialog-title">Hold this claim</h2>
      <p className="clm-dialog-body">It stays here, out of submission, until you bring it back.</p>
      <div className="clm-radio-list" role="radiogroup" aria-label="Reason">
        {HOLD_REASONS.map((r) => (
          <label key={r} className="clm-radio">
            <input type="radio" name="hold-reason" checked={reason === r} onChange={() => { setReason(r); }} />
            <span>{r}</span>
          </label>
        ))}
      </div>
      <div>
        <span className="clm-label">Note</span>
        <textarea
          className="clm-textarea"
          style={{ minHeight: 70 }}
          maxLength={140}
          value={note}
          onChange={(e) => { setNote(e.target.value); }}
          aria-label="Note"
        />
      </div>
      <div>
        <span className="clm-label">Bring it back on</span>
        <input type="date" className="clm-field" min={min} value={until} onChange={(e) => { setUntil(e.target.value); }} />
        <div className="clm-field-hint">Leave empty to keep it on hold until you open it.</div>
      </div>
      <div className="clm-dialog-actions">
        <button
          type="button"
          className="clm-btn clm-btn-primary"
          disabled={!reason}
          onClick={() => { onHold({ reason, ...(note ? { note } : {}), ...(until ? { until } : {}) }); }}
        >
          Hold claim
        </button>
        <button type="button" className="clm-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

// ---- Don't bill -------------------------------------------------------------

const DONT_BILL_REASONS = [
  'The patient is paying for this themselves',
  'It duplicates another claim',
  'It was entered by mistake',
  'Something else',
];

export function DontBillDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (v: { reason: string; other?: string }) => void;
}) {
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const needsOther = reason === 'Something else';
  const valid = reason && (!needsOther || other.trim());

  return (
    <Modal label="Don’t bill this claim?" onCancel={onCancel}>
      <h2 className="clm-dialog-title">Don’t bill this claim?</h2>
      <p className="clm-dialog-body">
        ELVA won’t submit it, and it won’t be sent from {PMS} either. The procedures stay on the patient’s record.
      </p>
      <div className="clm-radio-list" role="radiogroup" aria-label="Reason">
        {DONT_BILL_REASONS.map((r) => (
          <label key={r} className="clm-radio">
            <input type="radio" name="dontbill-reason" checked={reason === r} onChange={() => { setReason(r); }} />
            <span>{r}</span>
          </label>
        ))}
      </div>
      {needsOther ? (
        <input
          className="clm-field"
          placeholder="Tell ELVA why"
          aria-label="Reason detail"
          value={other}
          onChange={(e) => { setOther(e.target.value); }}
        />
      ) : null}
      <div className="clm-dialog-actions">
        <button
          type="button"
          className="clm-btn clm-btn-danger"
          disabled={!valid}
          onClick={() => { onConfirm({ reason, ...(other ? { other } : {}) }); }}
        >
          Don’t bill
        </button>
        <button type="button" className="clm-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

// ---- Leave (dirty) ----------------------------------------------------------

export function LeaveDialog({
  patient,
  onSave,
  onDiscard,
  onCancel,
}: {
  patient: string;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal label={`Save your changes to ${patient}?`} onCancel={onCancel}>
      <h2 className="clm-dialog-title">Save your changes to {patient}?</h2>
      <div className="clm-dialog-actions">
        <button type="button" className="clm-btn clm-btn-primary" onClick={onSave}>
          Save
        </button>
        <button type="button" className="clm-btn" onClick={onDiscard}>
          Discard
        </button>
        <button type="button" className="clm-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

// ---- Milestone --------------------------------------------------------------

export function MilestoneDialog({
  changedCount,
  onKeepReviewing,
  onOpenSettings,
}: {
  changedCount: number;
  onKeepReviewing: () => void;
  /** Never turns autopilot on from this page (D133) — just dismisses before the link navigates. */
  onOpenSettings: () => void;
}) {
  return (
    <Modal label="ELVA is ready to submit claims on its own" onCancel={onKeepReviewing} large>
      <p className="clm-eyebrow">{TRAINING_GOAL} of {TRAINING_GOAL} confirmed</p>
      <h2 className="clm-dialog-title">ELVA is ready to submit claims on its own</h2>
      <p className="clm-dialog-body">
        You and your team confirmed 200 claims. You changed {changedCount} of them, and ELVA learned from each change. In
        the last 50, you changed {MILESTONE_STATS.changedLast50}.
      </p>
      <div className="clm-milestone-panel">
        <span className="clm-train-label">With autopilot on</span>
        <ul className="clm-milestone-list">
          <li>· Claims ELVA is sure about are submitted without waiting for you.</li>
          <li>· Anything it isn’t sure about still comes here, with the reason.</li>
          <li>· Payers and procedures ELVA hasn’t seen enough of keep coming to you — today that’s {MILESTONE_STATS.notReadyList}.</li>
          <li>· You can turn it off at any time.</li>
        </ul>
      </div>
      <div className="clm-dialog-actions">
        <Link to="/settings/autopilot" className="clm-btn clm-btn-primary clm-btn-lg" onClick={onOpenSettings}>
          Open autopilot settings
        </Link>
        <button type="button" className="clm-btn clm-btn-lg" onClick={onKeepReviewing}>
          Keep reviewing for now
        </button>
      </div>
    </Modal>
  );
}
